import React, { useContext, useState } from "react"
import tw from "twin.macro"
import { Routes } from "../../routes"
import Link from "next/link"
import { useTranslation } from "next-i18next"
import { ReactComponent as DiscordIcon } from "../../assets/icons/discord.svg"
import { ReactComponent as YouTubeIcon } from "../../assets/icons/youTube.svg"
import { ReactComponent as InstagramIcon } from "../../assets/icons/instagram.svg"
import { ReactComponent as RedditIcon } from "../../assets/icons/reddit.svg"
import { ReactComponent as WebClientIcon } from "../../assets/icons/bonsai.svg"
import { getExpDays } from "../datetime"
import { AuthContext } from "../../utils/context"
import { Modal } from "../modal"
import { ReactComponent as FlagIcon } from "../../assets/question/flag.svg"
import { FlagModal } from "../question/flagModal"
import {
  Platform,
  QuestionDetailFragment,
  QuestionState,
  UpdateQuestionSetFlaggedMutationFn,
} from "../../graphql/generated/graphql"
import { ReactComponent as VerifiedSVG } from "../../assets/alert/verified.svg"
import { EmojiReactions } from "../emojiReactions"

interface PlatformProp {
  platform: Platform
  mediaUrl?: string
}
export const PlatformIcon = ({ platform, mediaUrl }: PlatformProp) => {
  const renderIcon = () => {
    switch (platform) {
      case Platform.Discord:
        return <DiscordIcon />
      case Platform.Reddit:
        return (
          <Link href={`${mediaUrl}`} passHref>
            <a target="_blank">
              <RedditIcon />
            </a>
          </Link>
        )
      case Platform.Youtube:
        return (
          <Link href={`${mediaUrl}`} passHref>
            <a target="_blank">
              <YouTubeIcon />
            </a>
          </Link>
        )
      case Platform.Instagram:
        return (
          <Link href={`${mediaUrl}`} passHref>
            <a target="_blank">
              <InstagramIcon />
            </a>
          </Link>
        )
      default:
      case Platform.WebClient:
        return <WebClientIcon />
    }
  }
  const { t } = useTranslation("question")
  return (
    <span tw="z-10 text-xs font-bold text-base-4 flex space-x-2 items-center" data-testid="platform">
      <span>{t("open.via")}</span>
      {renderIcon()}
    </span>
  )
}

interface QuestionCardProps {
  linked: boolean
  referringProfileUsername: string
  question: QuestionDetailFragment
  setFlagged?: UpdateQuestionSetFlaggedMutationFn
  refetchQuestion?: () => Promise<Record<string, any>>
}

export const QuestionCard = ({
  question,
  setFlagged,
  refetchQuestion,
  linked,
  referringProfileUsername,
}: QuestionCardProps) => {
  const { createdAt, Artist, Fan, platform, body, id, state, SourceComment } = question
  const expiresIn = getExpDays(createdAt)
  const fanUsername = Fan.username
  const { userId } = useContext(AuthContext)
  const isArtist = userId === Artist.id

  const { t } = useTranslation("question")
  const [showFlagModal, setShowFlagModal] = useState(false)
  const flagModalProps =
    refetchQuestion && setFlagged
      ? {
          show: showFlagModal,
          setShow: setShowFlagModal,
          component: <FlagModal {...{ setShowFlagModal, questionId: id, setFlagged, refetchQuestion }} />,
        }
      : undefined

  return (
    <div
      tw="flex flex-col border-base-2 border-2 rounded-[16px] p-4 py-2 w-full relative"
      css={[linked && tw`hover:(border-purple no-underline)`]}
      data-testid={`question-card`}
    >
      <div tw="flex flex-col space-y-3">
        <div tw="flex justify-between space-x-2 items-start">
          <h3>
            {linked ? (
              <Link
                href={{
                  pathname: `${Routes.ShowQuestion}`,
                  query: { id, profile: referringProfileUsername },
                }}
                passHref
              >
                <a data-testid="fan-profile-name" tw="no-underline after:(absolute top-0 bottom-0 left-0 right-0)">
                  <span tw="text-md text-base-6 block">{Fan.name}</span>
                </a>
              </Link>
            ) : (
              <Link href={{ pathname: Routes.Profile, query: { username: fanUsername } }} passHref>
                <a data-testid="fan-profile-name" tw="no-underline">
                  <span tw="text-md text-base-6 block">{Fan.name}</span>
                </a>
              </Link>
            )}
            <span tw="font-normal text-base-4 text-sm block w-full space-x-1">
              <span data-testid="artist-profile-name">{t("open.asked", { artist: Artist.name })}</span>
              {Artist.verified && (
                <span data-testid="verified-check" tw="inline-block">
                  <VerifiedSVG title="verified" tw="relative top-0.5 w-3 h-3" />
                </span>
              )}
            </span>
          </h3>
          {isArtist && state != QuestionState.Expired && !linked && (
            <>
              <span
                data-testid="report-question"
                tw="text-right text-red text-sm font-bold hover:cursor-pointer whitespace-nowrap"
                css="svg {display: inline-block; margin-right: 4px;}"
                onClick={() => setShowFlagModal(true)}
              >
                <FlagIcon />
                {t("open.report")}
              </span>
              {flagModalProps && <Modal {...flagModalProps} />}
            </>
          )}
        </div>
        <p tw="text-md text-base-6">{body}</p>
        <div tw="flex justify-between text-base-4 text-sm">
          <p tw="text-base-4 text-sm">
            {expiresIn > 2 && <span data-testid="expires-in">{t(`open.expires`, { number: expiresIn })}</span>}
            {expiresIn === 2 && <span data-testid="expires-in">{t(`open.tomorrow`)}</span>}
            {expiresIn === 1 && <span data-testid="expires-in">{t(`open.today`)}</span>}
            {expiresIn < 1 && <span data-testid="expired">{t(`open.expired`)}</span>}
          </p>
        </div>
      </div>
      <div tw="flex justify-between items-center border-t border-base-2 pt-2 mt-2">
        <EmojiReactions {...{ question, refetchQuestion, isReactable: true, useEmojiSmall: false }} />
        {platform && <PlatformIcon {...{ mediaUrl: SourceComment?.mediaUrl, platform }} />}
      </div>
    </div>
  )
}
